import type { RetryOptions } from '@/scripts/utils/queue';
import { retry, useQueue } from '@/scripts/utils/queue';
import { useRefs } from '@/scripts/hooks';
import { useSessionContext } from '@/contexts/session/context';
import { GroupStatus } from '@/configs/apis/groups/interface';
import { safeJson } from '@/scripts/utils';
import { groupsApis } from '@/configs/apis/groups';
import { chatApis } from '@/configs/apis/chat';
import { getAvatar } from '@/scripts/utils/genAvatar';
import type {
  SendParams,
  ChatMessagesArgMessageIdParams,
  ChatMessagesLoadParams,
  RetryParams,
  ChatMessage,
  ChatMessagePartial,
} from '../hooks/useChatMessages/types';
import {
  ChatMessageStatus,
  ChatMessageCategory,
  ChatMessagePayloadType,
} from '../hooks/useChatMessages/types';
import {
  pushMessagesArgs,
  eachMessagesArgs,
  flatMessagesArgs,
  isSystemMessage,
  parseMessageId,
  isGroupSystemMessage,
  isGroupUpdateSystemMessage,
} from '../hooks/useChatMessages/utils';
import type { Chat } from '../hooks/useChats/types';
import { ChatCategory } from '../hooks/useChats/types';
import {
  ChatSocketControls,
  NoticeCode,
  PushType,
  SendMessageResponseStatusCode,
} from '../hooks/useChatSocket/types';
import type { ChatContextHooksFull, ChatContextHooksProps } from '../../context';
import type { ChatGroupMemberId } from '../hooks/useChatGroup/types';
import { serverTime } from '../hooks/useSystemTime/utils';
import type {
  SendMessageToServerParams,
  ChatMessagesArgMessageIdPullParams,
} from './messages';

export type UseChatGroupMessagesValueResult = ReturnType<
  typeof useChatGroupMessagesValue
>;

export function useChatGroupMessagesValue({ hooks }: ChatContextHooksProps) {
  const { chatSocket, chats, chatMessages, chatGroup, groupEncryption } =
    hooks as ChatContextHooksFull;
  const {
    session: { user },
  } = useSessionContext();
  const [loading, setLoading] = useState(true);

  const { socket, event, loading: socketLoading, send } = chatSocket;

  const queue = useQueue();
  const statusQueue = useQueue();

  const sendGroupMessageToServer = useCallback(
    async (params: SendMessageToServerParams) => {
      const {
        chatMessagesValue: { sendMessageToServer },
        groupEncryption,
      } = hooks as ChatContextHooksFull;

      const members = await chatGroup.getMembers({
        group_id: params.sendParams.chat.chat_id,
        offset: 0,
        limit: -1,
        include_removed: false,
      });

      const expect_read_list: ChatGroupMemberId[] = [];

      members.forEach((item) => {
        if (item.user_id !== user!.userId) {
          expect_read_list.push(item.user_id);
        }
      });

      Object.assign(params.sendParams.message, {
        expect_read_list,
      });

      return sendMessageToServer(
        {
          ...params,
          handlers: {
            ...params?.handlers,
            async encrypt(message) {
              const {
                sendParams: { chat },
              } = params;
              const { ciphertext, epoch } = await groupEncryption.encryptMessage(
                chat.chat_id,
                message.payload!
              );
              return {
                ...message,
                payload: ciphertext,
                encrypt_index: epoch,
              };
            },
            async updateMessageBefore(message, _res) {
              await chatGroup.initReadStateReceiver({
                message_id: message.message_id!,
                expect_read_list: expect_read_list?.join(','),
              });

              return {
                ...message,
                status: ChatMessageStatus.UNREAD,
              };
            },
          },
        },
        (err) => {
          const isNotMember = chatSocket.equalityErrorCode(
            err,
            SendMessageResponseStatusCode.NOT_GROUP_MEMBER
          );

          if (isNotMember) {
            chatGroup.removeMembers(
              [
                {
                  id: params.sendParams.chat.chat_id,
                  args: [params.message.owner_id],
                },
              ],
              false
            );
            return false;
          }

          return true;
        }
      );
    },
    [chatSocket, chatGroup, hooks, user]
  );

  const sendGroupMessage = useCallback(
    async (params: SendParams) => {
      const {
        chatMessagesValue: { sendMessageCommon },
      } = hooks as ChatContextHooksFull;
      await sendMessageCommon(params, sendGroupMessageToServer);
    },
    [sendGroupMessageToServer, hooks]
  );

  const retryGroupMessage = useCallback(
    async (params: RetryParams) => {
      const {
        chatMessagesValue: { retryMessage },
      } = hooks as ChatContextHooksFull;
      return retryMessage(params, sendGroupMessageToServer);
    },
    [hooks, sendGroupMessageToServer]
  );

  const getLatestGroupInfo = useCallback(
    async (group_id: string) => {
      const data = await groupsApis.getGroupInfo(group_id);
      if (data?.gid) {
        const { group_name, status, group_head } = data;
        const avatar = getAvatar(group_head, group_name);
        await chats.updateChats([{ chat_id: group_id, name: group_name, avatar }]);
        await chatGroup.updateGroups([{ group_id, status, name: group_name, avatar }]);
        await chatGroup.insertSettings([
          { group_id, group_name: group_name, group_head: avatar },
        ]);
      } else {
        await chatGroup.updateGroups([{ group_id, status: GroupStatus.DISBAND }]);
      }
    },
    [chatGroup, chats]
  );

  const getLatestGroupMemberList = useCallback(
    async (group_id: string) => {
      const membersMap = chatGroup.chatGroupState.state.membersMap;
      const normalMemberList = membersMap[group_id]?.normalMembers;

      const members = await retry(() => groupsApis.getGroupMemberList(group_id), {
        maxCount: 5,
      });
      // const membersAdded = members?.filter(
      //   (member) => !normalMemberList?.some((m) => m.user_id === member.user_id)
      // );
      const membersRemoved = normalMemberList?.filter(
        (member) => !members?.some((m) => m.user_id === member.user_id)
      );
      if (members?.length > 0) {
        chatGroup.insertMembers([{ id: group_id, args: members }], false);
      }
      if (membersRemoved?.length > 0) {
        chatGroup.removeMembers(
          [
            {
              id: group_id,
              args: membersRemoved.map((m) => m.user_id),
            },
          ],
          false
        );
      }
    },
    [chatGroup]
  );

  const updateGroupInfo = useCallback(
    async (paramsList: ChatMessagesArgMessageIdParams[]) => {
      await Promise.all(
        paramsList.map(({ chat_id, messages }) =>
          Promise.all(
            messages.map(async (message) => {
              const {
                user_id,
                member: member_,
                group_name,
                group_id: group_id_,
              } = safeJson(message.payload || '{}', {} as any);

              let groupInfo = null;
              const group_id: string = group_id_ || chat_id;
              const member = member_ || [];
              const {
                chatMediaCall: { getChatMediaInfo },
                chatMediaValue: { mediaExit },
              } = hooks as ChatContextHooksFull;

              const chatMediaInfo = await getChatMediaInfo(group_id);

              if (message.receiverId === user?.userId) {
                groupInfo = chatGroup.chatGroupState.getGroup(group_id);
              }

              async function exitMedia() {
                if (chatMediaInfo) {
                  await mediaExit(chatMediaInfo);
                }
              }

              switch (message.payload_type) {
                case ChatMessagePayloadType.GROUP_UPDATE: {
                  await chatGroup.insertSettings([{ group_id, group_name }]);
                  await chatGroup.updateGroups([{ group_id, name: group_name }]);
                  await chats.updateChats([{ chat_id: group_id, name: group_name }]);
                  break;
                }
                case ChatMessagePayloadType.GROUP_MEMBER_QUIT: {
                  if (groupInfo?.is_encryption) {
                    await groupEncryption.deleteGroup(group_id);
                  }
                  if (user_id === user?.userId) {
                    await exitMedia();
                  } else {
                    await chatGroup.removeMembers(
                      [
                        {
                          id: group_id,
                          args: [user_id],
                        },
                      ],
                      false
                    );
                  }
                  break;
                }
                case ChatMessagePayloadType.GROUP_REMOVE_MEMBER: {
                  let hasMe = false;
                  const ids = member.map((item: any) => {
                    if (item.user_id === user?.userId) {
                      hasMe = true;
                    }
                    return item.user_id;
                  });

                  await chatGroup.removeMembers(
                    [
                      {
                        id: group_id,
                        args: ids,
                      },
                    ],
                    false
                  );

                  if (groupInfo?.is_encryption) {
                    await groupEncryption.deleteGroup(group_id);
                  }

                  if (hasMe) {
                    await exitMedia();
                  }

                  break;
                }
                case ChatMessagePayloadType.GROUP_INVITE: {
                  const {
                    groupChatsValue: { syncGroupSettings },
                  } = hooks as ChatContextHooksFull;
                  getLatestGroupInfo(group_id);
                  getLatestGroupMemberList(group_id);
                  syncGroupSettings(group_id);
                  break;
                }
                case ChatMessagePayloadType.GROUP_DISBAND: {
                  await chatGroup.updateGroups([
                    { group_id: group_id, status: GroupStatus.DISBAND },
                  ]);
                  await exitMedia();
                  break;
                }
                default: {
                  break;
                }
              }
            })
          )
        )
      );
    },
    [
      chatGroup,
      chats,
      getLatestGroupInfo,
      getLatestGroupMemberList,
      groupEncryption,
      hooks,
      user?.userId,
    ]
  );

  const pullGroupMessageStatuses = useCallback(
    (serverSequence: number, retryOptions?: RetryOptions) => {
      const type = ChatCategory.GROUP_MSG_STATUS;
      return chatApis
        .getSequence(type, serverSequence)
        .then((sequence) => {
          return send(
            {
              headers: {
                control: ChatSocketControls.PULL_GROUP_MSG_STATUS,
              },
              body: {
                sequence,
              },
            },
            retryOptions
          )
            .then((res) => {
              const list: any[] = res.body.list;

              const { args, push } = pushMessagesArgs();

              list.forEach((item: any) => {
                const message = parseMessageId(item.messageId);
                push(
                  {
                    ...item,
                    ...message,
                    cli_msg_id: item.cliMsgId || message.cliMsgId,
                  },
                  item.chatId
                );
              });

              return {
                res,
                args,
              };
            })
            .then(({ res, args }) => {
              const sequence: number | undefined = res.body.maxSequence || undefined;
              return Object.assign(res, {
                args,
                sequence,
                async saveSequence() {
                  if (sequence !== undefined) {
                    await chatApis.setSequence(type, sequence);
                  }
                },
              });
            });
        })
        .catch((err) => {
          if (err?.code === 'SEQUENCE_IS_OLD') {
            return null;
          }
          throw err;
        });
    },
    [send]
  );

  const addRemoveMessages = useCallback(
    async (paramsList: ChatMessagesArgMessageIdParams[]) => {
      const {
        chatMessagesValue: { addMessage },
      } = hooks as ChatContextHooksFull;

      await Promise.all(
        paramsList.map(({ chat_id, messages }) =>
          Promise.all(
            messages.map(async (message) => {
              const { group_id: group_id_ } = safeJson(
                message.payload || '{}',
                {} as any
              );

              const group_id: string = group_id_ || chat_id;

              switch (message.payload_type) {
                case ChatMessagePayloadType.GROUP_DISBAND:
                case ChatMessagePayloadType.GROUP_REMOVE_MEMBER:
                case ChatMessagePayloadType.GROUP_MEMBER_QUIT: {
                  const chat = await chats.getChat({ chat_id: group_id });
                  if (chat) {
                    const message_: ChatMessagePartial = {
                      ...message,
                      category: ChatMessageCategory.RECEIVE,
                      status: ChatMessageStatus.READ,
                      expire_duration: chat.expire_duration,
                      read_at: serverTime.time,
                    };
                    delete message_.cli_msg_id;
                    await addMessage({
                      chat_id: group_id,
                      message: message_ as ChatMessage,
                      updateLastRead: false,
                    });
                  }
                  break;
                }
              }
            })
          )
        )
      );
    },
    [hooks, chats]
  );

  const pullGroupMessages = useCallback(
    async (params: ChatMessagesArgMessageIdPullParams[]) => {
      const {
        chatMessagesValue: { pullMessagesCommon, updateLastReadMessages },
        groupEncryption,
        chatMediaGroupValue: { queryGroupMediaCalling },
      } = hooks as ChatContextHooksFull;

      const result = await pullMessagesCommon(ChatCategory.GROUP, params, {
        async pulled(pulledMessage, { chat_id, isNew }) {
          const isGroupSysMsg = isGroupSystemMessage(pulledMessage);

          if (isGroupSysMsg) {
            pulledMessage.read_at = pulledMessage.create_at;

            if (
              pulledMessage.payload_type ===
              ChatMessagePayloadType.GROUP_ENCRYPTION_COMMIT
            ) {
              groupEncryption.handleApplyCommitQueue(chat_id, pulledMessage);
            } else if (
              pulledMessage.payload_type === ChatMessagePayloadType.GROUP_CREATE
            ) {
              const groupInfo = chatGroup.chatGroupState.getGroup(chat_id);
              if (groupInfo?.is_encryption) {
                await groupEncryption.initCreateGroupEncryption(pulledMessage);
              }
            } else if (
              pulledMessage.payload_type === ChatMessagePayloadType.GROUP_UPDATE_INFO
            ) {
              await getLatestGroupInfo(chat_id);
            } else if (isGroupUpdateSystemMessage(pulledMessage)) {
              if (isNew) {
                if (
                  pulledMessage.payload_type === ChatMessagePayloadType.GROUP_INVITE &&
                  pulledMessage.status === ChatMessageStatus.UNREAD
                ) {
                  queryGroupMediaCalling([{ chat_id } as Chat]);
                }
              } else {
                await updateGroupInfo([{ chat_id, messages: [pulledMessage] }]);
              }
            }
          } else {
            if (pulledMessage.payload_type === ChatMessagePayloadType.ENCRYPTED_TEXT) {
              pulledMessage.payload = await groupEncryption.decryptMessage(
                chat_id,
                pulledMessage
              );
            }
            await chatGroup.initReadStateReceiver({
              message_id: pulledMessage.message_id,
              expect_read_list: pulledMessage.expectReadList?.join(','),
            });
          }

          return {
            status: ChatMessageStatus.UNREAD,
            ...pulledMessage,
          };
        },
      });

      // Update for self system message. Because userId on payload.
      await updateLastReadMessages(result.pulledListSender);

      return result;
    },
    [hooks, chatGroup, getLatestGroupInfo, updateGroupInfo]
  );

  const pullGroup = useCallback(
    async (serverSequence: number) =>
      queue.push(() => {
        const {
          chatMessagesValue: { pullCommon },
          groupChatsValue: { loadGroupChatsByChatId },
          chatMediaGroupValue: { mediaStatusUpdateGroup },
        } = hooks as ChatContextHooksFull;
        return pullCommon(ChatCategory.GROUP, serverSequence, {
          loadChats: loadGroupChatsByChatId,
          pullMessagesBefore(messages) {
            return messages.filter(
              (msg) => 200 <= msg.payload_type! && msg.payload_type! < 300
            );
          },
          pullMessages: pullGroupMessages,
          async systemMessagesHandler(result) {
            await mediaStatusUpdateGroup(result.pulledList);
          },
        });
      }),
    [hooks, pullGroupMessages, queue]
  );

  const pullGroupMsgStatus = useCallback(
    async (serverSequence: number) => {
      const result = await statusQueue.push(async () => {
        const {
          chatMessagesValue: { updateLastReadMessages },
        } = hooks as ChatContextHooksFull;
        const result = await pullGroupMessageStatuses(serverSequence);
        if (result) {
          const { args, saveSequence } = result;

          const sendList = pushMessagesArgs<ChatMessagesArgMessageIdParams>();

          await eachMessagesArgs(args, async (msg, chat_id) => {
            const msg_ = await chatMessages.getMessage(chat_id, msg);
            switch (msg_?.category) {
              case ChatMessageCategory.SEND:
                if (!isSystemMessage(msg)) {
                  Object.assign(msg, {
                    status: ChatMessageStatus.PARTIAL_READ,
                  });
                  sendList.push(msg, chat_id);
                }
                break;
              case ChatMessageCategory.RECEIVE:
                Object.assign(msg, {
                  read_at: msg.createdAt,
                });
                break;
            }
          });

          if (sendList.args.length) {
            const paramsList = flatMessagesArgs(sendList.args).map((m) => ({
              message_id: m.message_id!,
              args: [m.senderId!],
            }));

            await chatGroup.insertReadStateReader(paramsList);

            await eachMessagesArgs(sendList.args, async (msg) => {
              const readState = await chatGroup.getReadState(msg.message_id!);
              if (
                readState?.reader_ids_value.length &&
                readState?.expect_read_list_value?.length &&
                readState?.reader_ids_value.length >=
                  readState?.expect_read_list_value?.length
              ) {
                Object.assign(msg, {
                  status: ChatMessageStatus.READ,
                  read_at: msg.createdAt,
                });
              }
            });
          }

          try {
            // Update for group message status
            await updateLastReadMessages(args);

            await chatMessages.updateMessages(args);
          } catch (err) {
            console.error(err);
          }

          await saveSequence();

          return result;
        }
      });

      if (result?.body.list.length) {
        await pullGroupMsgStatus(Infinity);
      }
    },
    [pullGroupMessageStatuses, chatGroup, chatMessages, hooks, statusQueue]
  );

  const loadGroupMessages = useCallback(
    (params: ChatMessagesLoadParams) => {
      const {
        chatMessagesValue: { loadMessages },
      } = hooks as ChatContextHooksFull;
      return loadMessages(
        params,
        async (arg) => (await pullGroupMessages([arg])).list[0]
      );
    },
    [hooks, pullGroupMessages]
  );

  const refs = useRefs({
    chats,
    pullGroup,
    pullGroupMsgStatus,
    hooks: hooks as ChatContextHooksFull,
  });

  useLayoutEffect(() => {
    if (socket) {
      setLoading(true);

      refs.current
        .pullGroup(Infinity)
        .then(() => {
          return refs.current.pullGroupMsgStatus(Infinity);
        })
        .finally(() => {
          setLoading(false);
        });

      const fns = [
        event.onMessage(
          ({ body }) => {
            refs.current.pullGroup(body.sequence);
          },
          {
            control: ChatSocketControls.NOTICE_MSG,
            code: NoticeCode.NEW_MSG,
            msgType: ChatCategory.GROUP,
          }
        ),
        event.onMessage(
          ({ body }) => {
            refs.current.pullGroupMsgStatus(body.sequence);
          },
          {
            control: ChatSocketControls.NOTICE_MSG,
            code: NoticeCode.NEW_MSG,
            msgType: ChatCategory.GROUP_MSG_STATUS,
          }
        ),
        event.onMessage(
          ({ body }) => {
            const { chat_id } = safeJson(body.content, { chat_id: '' });
            if (chat_id) {
              refs.current.hooks.groupChatsValue.syncGroupSettings(chat_id);
            }
          },
          {
            control: ChatSocketControls.DIRECT_PUSH,
            pushType: PushType.EXPIRE_DURATION_NOTICE_GROUP,
          }
        ),
        (() => {
          const timer = setInterval(
            () => {
              refs.current.pullGroup(Infinity);
              refs.current.pullGroupMsgStatus(Infinity);
            },
            2 * 60 * 1000
          );
          return () => {
            clearTimeout(timer);
          };
        })(),
      ];

      return () => {
        fns?.forEach((fn) => fn());
      };
    } else {
      setLoading(socketLoading);
    }
  }, [refs, socket, event, socketLoading]);

  return useMemo(
    () => ({
      loading,
      sendGroupMessageToServer,
      sendGroupMessage,
      retryGroupMessage,
      pullGroupMessages,
      pullGroup,
      pullGroupMsgStatus,
      loadGroupMessages,
      updateGroupInfo,
      addRemoveMessages,
      getLatestGroupInfo,
      getLatestGroupMemberList,
    }),
    [
      loading,
      loadGroupMessages,
      pullGroup,
      pullGroupMessages,
      pullGroupMsgStatus,
      retryGroupMessage,
      sendGroupMessage,
      sendGroupMessageToServer,
      updateGroupInfo,
      addRemoveMessages,
      getLatestGroupInfo,
      getLatestGroupMemberList,
    ]
  );
}
